import { SpecialRateTickets, SpecialRateProgram, SpecialRateProgramUsage, SpecialRateTicketSaveRequest, SpecialRateTicketUpdateSaveRequest, SpecialRateTicketPaymentSaveRequest, DBTicket, RateOutput, Reservation, ReservationsDiagnostics, CreateReservationByEventSaveRequest, CreateReservationUsingEventSourceEnum, UserProfile, EventDetails, type TicketFieldRequirementsConfig } from '@parkingbase/ecommerce-api-dev';
import { ParkingValidationService, type ImportTemplateDownload } from '@/services/parking-validation.service';
import { GarageService } from '@/services/garage.service';


import type { TicketSearchResult } from '@/components/myaccount/parking-validation/change-discount/ChangeDiscount.types';
import type { AxiosResponse, RawAxiosRequestConfig } from 'axios';
import { create } from 'zustand';
import { logger } from '@/lib/logger';

// Monotonic token for report-month fetches. Only the most recent
// fetchTicketsByMonth request may apply its results / clear the loading flag,
// so a slower earlier-month response can't clobber a newer selection — the
// Reports month round-trip bug (PB-4303).
let reportFetchSeq = 0;

interface ImportSummary {
  total: number;
  valid: number;
  invalid: number;
  new: number;
  updated: number;
  deleted: number;
}

interface ParkingValidationState {
  // Active tickets
  tickets: SpecialRateTickets[];
  selectedTicket: SpecialRateTickets | null;
  ticketsLoading: boolean;
  specialRatePrograms: SpecialRateProgram[];

  // Events
  events: EventDetails[];
  eventsLoading: boolean;
  /** True once `fetchEvents` has settled at least once (success or failure) — PB-3280. */
  hasLoadedEvents: boolean;
  /** User the cached events belong to; a different user means they must be re-fetched. */
  eventsUserId: string | null;

  // Event reservations
  reservations: Reservation[];
  selectedReservation: Reservation | null;
  reservationsLoading: boolean;
  selectedEventId: string | null;
  // Bumped on every successful reservation mutation (create/update/cancel/import).
  // useEventParking subscribes to it and force-refetches, bypassing its per-params
  // fetch dedupe so the just-changed list reloads (PB-3071).
  reservationsRefreshToken: number;

  // Reservation vehicle field requirements (PB-2555) — resolved per garage+company from the API.
  reservationFieldRequirements: TicketFieldRequirementsConfig | null;

  // Reports
  reportTickets: SpecialRateTickets[];
  reportMonth: Date;
  reportLoading: boolean;
  pdfLoading: boolean;
  xlsxLoading: boolean;

  // Programs
  programs: SpecialRateProgram[];
  selectedProgram: SpecialRateProgram | null;
  programUsers: UserProfile[];
  programDomains: string[];
  programsLoading: boolean;
  userManagementLoading: boolean;

  // Usage
  specialRateUsage: SpecialRateProgramUsage[];

  // Import Reservations
  importDiagnostics: ReservationsDiagnostics[];
  importLoading: boolean;
  importSummary: ImportSummary | null;

  // Change Discount
  changeDiscountTicket: TicketSearchResult | null;
  changeDiscountLoading: boolean;
  changeDiscountApplying: boolean;

  // Validation Session (PB-2292)
  validationSession: {
    ticket: TicketSearchResult | null;
    selectedProgram: SpecialRateProgram | null;
    chargeToAccountEnabled: boolean;
  };

  // Remove Discount
  removeDiscountLoading: boolean;

  // Charge to Account
  chargeToAccountLoading: boolean;

  // Actions — Active Tickets
  fetchTickets: (options?: RawAxiosRequestConfig) => Promise<void>;
  searchTicket: (
    ticketNumber?: string,
    ticketId?: string,
    licensePlate?: string,
    options?: RawAxiosRequestConfig
  ) => Promise<DBTicket | null>;
  addTicket: (
    request: SpecialRateTicketSaveRequest,
    options?: RawAxiosRequestConfig
  ) => Promise<DBTicket | null>;
  updateTicket: (
    request: SpecialRateTicketUpdateSaveRequest,
    options?: RawAxiosRequestConfig
  ) => Promise<DBTicket | null>;
  payTicket: (
    request: SpecialRateTicketPaymentSaveRequest,
    options?: RawAxiosRequestConfig
  ) => Promise<DBTicket | null>;
  setSelectedTicket: (ticket: SpecialRateTickets | null) => void;

  // Actions — Events
  fetchEvents: (options?: RawAxiosRequestConfig) => Promise<void>;
  /** Drops another user's cached events so the nav waits for this user's (PB-3280). */
  resetEventsForUser: (userId: string) => void;

  // Actions — Event Reservations
  fetchReservations: (
    start: string,
    end: string,
    eventId?: string,
    options?: RawAxiosRequestConfig
  ) => Promise<void>;
  createReservationByEvent: (
    request: CreateReservationByEventSaveRequest,
    options?: RawAxiosRequestConfig
  ) => Promise<Reservation | null>;
  updateReservation: (
    id: string,
    reservation: Reservation,
    options?: RawAxiosRequestConfig
  ) => Promise<void>;
  cancelReservation: (id: string, options?: RawAxiosRequestConfig) => Promise<void>;
  setSelectedReservation: (reservation: Reservation | null) => void;
  setSelectedEventId: (eventId: string | null) => void;
  fetchReservationFieldRequirements: (
    garageId: string,
    company: string,
    options?: RawAxiosRequestConfig
  ) => Promise<void>;

  // Actions — Import Reservations
  downloadImportTemplate: (
    company: string,
    eventId: string,
    options?: RawAxiosRequestConfig
  ) => Promise<ImportTemplateDownload | null>;
  runImportDiagnostics: (
    company: string,
    file: File[],
    garageId?: string,
    eventId?: string,
    options?: RawAxiosRequestConfig
  ) => Promise<void>;
  executeImport: (
    company: string,
    garageId?: string,
    eventId?: string,
    options?: RawAxiosRequestConfig
  ) => Promise<void>;
  clearImportState: () => void;

  // Actions — Reports
  fetchTicketsByMonth: (month?: string, options?: RawAxiosRequestConfig) => Promise<void>;
  exportPdf: (month?: string, options?: RawAxiosRequestConfig) => Promise<AxiosResponse<File> | null>;
  exportXlsx: (
    month?: string,
    options?: RawAxiosRequestConfig
  ) => Promise<AxiosResponse<File> | null>;
  setReportMonth: (month: Date) => void;

  // Actions — Programs
  fetchProgramUsers: (
    companyId: string,
    discountProgram?: string,
    options?: RawAxiosRequestConfig
  ) => Promise<void>;
  assignUser: (
    companyId: string,
    userId: string,
    roles: string[],
    discountProgram?: string,
    options?: RawAxiosRequestConfig
  ) => Promise<UserProfile | null>;
  updateUserRoles: (
    companyId: string,
    userId: string,
    roles: string[],
    discountProgram?: string,
    options?: RawAxiosRequestConfig
  ) => Promise<UserProfile | null>;
  removeUser: (
    companyId: string,
    userId: string,
    discountProgram?: string,
    options?: RawAxiosRequestConfig
  ) => Promise<UserProfile | null>;
  setSelectedProgram: (program: SpecialRateProgram | null) => void;

  // Actions — Usage
  fetchSpecialRateUsage: (options?: RawAxiosRequestConfig) => Promise<void>;

  // Actions — Change Discount
  searchTicketForChange: (
    ticketId: string,
    options?: RawAxiosRequestConfig
  ) => Promise<TicketSearchResult | null>;
  applyNewDiscount: (
    request: SpecialRateTicketSaveRequest,
    options?: RawAxiosRequestConfig
  ) => Promise<DBTicket | null>;
  clearChangeDiscountState: () => void;

  // Actions — Remove Discount
  removeDiscount: (
    request: SpecialRateTicketUpdateSaveRequest,
    options?: RawAxiosRequestConfig
  ) => Promise<DBTicket | null>;

  // Actions — Charge to Account
  chargeToAccount: (
    ticketId: string,
    accountId: string,
    amount: number,
    exitBy: string,
    company?: string,
    options?: RawAxiosRequestConfig
  ) => Promise<DBTicket | null>;
  getOpenTicketRate: (
    ticketId: string,
    exitBy: string,
    company?: string,
    options?: RawAxiosRequestConfig
  ) => Promise<RateOutput | null>;

  // Actions — Validation Session (PB-2292)
  searchTicketForValidation: (
    ticketNumber?: string,
    ticketId?: string,
    licensePlate?: string,
    options?: RawAxiosRequestConfig
  ) => Promise<TicketSearchResult | null>;
  setValidationSelectedProgram: (program: SpecialRateProgram | null) => void;
  clearValidationSession: () => void;
}

export const useParkingValidationStore = create<ParkingValidationState>((set, get) => ({
  // ── Initial State ───────────────────────────────────────────────────────

  tickets: [],
  selectedTicket: null,
  ticketsLoading: false,
  specialRatePrograms: [],

  events: [],
  eventsLoading: false,
  hasLoadedEvents: false,
  eventsUserId: null,

  reservations: [],
  selectedReservation: null,
  reservationsLoading: false,
  selectedEventId: null,
  reservationsRefreshToken: 0,
  reservationFieldRequirements: null,

  reportTickets: [],
  reportMonth: new Date(),
  reportLoading: false,
  pdfLoading: false,
  xlsxLoading: false,

  programs: [],
  selectedProgram: null,
  programUsers: [],
  programDomains: [],
  programsLoading: false,
  userManagementLoading: false,

  specialRateUsage: [],

  importDiagnostics: [],
  importLoading: false,
  importSummary: null,

  changeDiscountTicket: null,
  changeDiscountLoading: false,
  changeDiscountApplying: false,

  removeDiscountLoading: false,

  chargeToAccountLoading: false,

  validationSession: { ticket: null, selectedProgram: null, chargeToAccountEnabled: false },

  // ── Active Tickets ──────────────────────────────────────────────────────

  fetchTickets: async (options?) => {
    try {
      set({ ticketsLoading: true });
      const service = new ParkingValidationService();
      const tickets = await service.getTickets(options);
      // Sync selectedTicket with refreshed data so detail panel stays current
      const current = get().selectedTicket;
      const updatedSelected = current
        ? tickets.find((t: SpecialRateTickets) => t.ticketId === current.ticketId) ?? null
        : null;
      set({ tickets, selectedTicket: updatedSelected });
    } catch (error) {
      logger.error('Failed to fetch tickets', error, {
        feature: 'parking-validation',
        route: 'fetchTickets',
      });
    } finally {
      set({ ticketsLoading: false });
    }
  },

  searchTicket: async (ticketNumber?, ticketId?, licensePlate?, options?) => {
    try {
      const service = new ParkingValidationService();
      return await service.searchTicket(ticketNumber, ticketId, licensePlate, options);
    } catch (error) {
      logger.error('Failed to search ticket', error, {
        feature: 'parking-validation',
        route: 'searchTicket',
      });
      return null;
    }
  },

  addTicket: async (request, options?) => {
    try {
      set({ ticketsLoading: true });
      const service = new ParkingValidationService();
      const result = await service.addTicket(request, options);
      return result;
    } catch (error) {
      logger.error('Failed to add ticket', error, {
        feature: 'parking-validation',
        route: 'addTicket',
      });
      throw error;
    } finally {
      set({ ticketsLoading: false });
    }
  },

  updateTicket: async (request, options?) => {
    // Does NOT toggle ticketsLoading — that flag is reserved for the ticket-list
    // fetch (fetchTickets) so the list loader shows exactly once, during the
    // refresh, not also during the preceding update. Callers that need a
    // per-action saving indicator (e.g. the exit-time picker's Done button) own
    // their own flag (PB-1697).
    try {
      const service = new ParkingValidationService();
      const result = await service.updateTicket(request, options);
      return result;
    } catch (error) {
      logger.error('Failed to update ticket', error, {
        feature: 'parking-validation',
        route: 'updateTicket',
      });
      throw error;
    }
  },

  payTicket: async (request, options?) => {
    try {
      const service = new ParkingValidationService();
      const result = await service.payTicket(request, options);
      return result;
    } catch (error) {
      logger.error('Failed to pay ticket', error, {
        feature: 'parking-validation',
        route: 'payTicket',
      });
      throw error;
    }
  },

  setSelectedTicket: (ticket) => set({ selectedTicket: ticket }),

  // ── Events ─────────────────────────────────────────────────────────────

  fetchEvents: async (options?) => {
    // Dedupe concurrent callers (e.g. the layout-level visibility hook mounted
    // in more than one layout) — the first call wins, the rest bail.
    if (get().eventsLoading) return;
    // A user switch mid-request must not land the previous user's events (PB-3280).
    const forUser = get().eventsUserId;
    const isCurrent = () => get().eventsUserId === forUser;
    try {
      set({ eventsLoading: true });
      const service = new ParkingValidationService();
      const events = await service.getEventsForUser(options);
      if (isCurrent()) set({ events });
    } catch (error) {
      logger.error('Failed to fetch events', error, {
        feature: 'parking-validation',
        route: 'fetchEvents',
      });
    } finally {
      if (isCurrent()) set({ eventsLoading: false, hasLoadedEvents: true });
    }
  },

  resetEventsForUser: (userId) => {
    // Clearing eventsLoading lets the new user's fetch start; the stale one is discarded above.
    set({ events: [], hasLoadedEvents: false, eventsLoading: false, eventsUserId: userId });
  },

  // ── Event Reservations ──────────────────────────────────────────────────

  fetchReservations: async (start, end, eventId?, options?) => {
    try {
      set({ reservationsLoading: true });
      const service = new ParkingValidationService();
      const reservations = await service.getReservationsByEvent(start, end, eventId, options);
      set({ reservations });
    } catch (error) {
      logger.error('Failed to fetch reservations', error, {
        feature: 'parking-validation',
        route: 'fetchReservations',
      });
    } finally {
      set({ reservationsLoading: false });
    }
  },

  createReservationByEvent: async (request, options?) => {
    try {
      set({ reservationsLoading: true });
      const service = new ParkingValidationService();
      const result = await service.createReservationByEvent(CreateReservationUsingEventSourceEnum.RESERVATION, request, options);
      set((s) => ({ reservationsRefreshToken: s.reservationsRefreshToken + 1 }));
      return result;
    } catch (error) {
      logger.error('Failed to create reservation', error, {
        feature: 'parking-validation',
        route: 'createReservationByEvent',
      });
      throw error;
    } finally {
      set({ reservationsLoading: false });
    }
  },

  updateReservation: async (id, reservation, options?) => {
    try {
      set({ reservationsLoading: true });
      const service = new ParkingValidationService();
      await service.updateReservation(id, reservation, options);
      set((s) => ({ reservationsRefreshToken: s.reservationsRefreshToken + 1 }));
    } catch (error) {
      logger.error('Failed to update reservation', error, {
        feature: 'parking-validation',
        route: 'updateReservation',
      });
      throw error;
    } finally {
      set({ reservationsLoading: false });
    }
  },

  cancelReservation: async (id, options?) => {
    try {
      set({ reservationsLoading: true });
      const service = new ParkingValidationService();
      await service.cancelReservation(id, options);
      set((s) => ({ reservationsRefreshToken: s.reservationsRefreshToken + 1 }));
    } catch (error) {
      logger.error('Failed to cancel reservation', error, {
        feature: 'parking-validation',
        route: 'cancelReservation',
      });
      throw error;
    } finally {
      set({ reservationsLoading: false });
    }
  },

  setSelectedReservation: (reservation) => set({ selectedReservation: reservation }),
  setSelectedEventId: (eventId) => set({ selectedEventId: eventId }),

  fetchReservationFieldRequirements: async (garageId, company, options?) => {
    // Clear first so an in-flight fetch falls back to the Theme default rather than showing a prior garage's requirements.
    set({ reservationFieldRequirements: null });
    try {
      const service = new GarageService();
      const config = await service.getTicketFieldRequirements(garageId, company, options);
      set({ reservationFieldRequirements: config });
    } catch (error) {
      logger.error('Failed to fetch reservation field requirements', error, {
        feature: 'parking-validation',
        route: 'fetchReservationFieldRequirements',
      });
    }
  },

  // ── Import Reservations ─────────────────────────────────────────────────

  downloadImportTemplate: async (company, eventId, options?) => {
    try {
      set({ importLoading: true });
      const service = new ParkingValidationService();
      return await service.downloadImportTemplate(company, eventId, options);
    } catch (error) {
      logger.error('Failed to download import template', error, {
        feature: 'parking-validation',
        route: 'downloadImportTemplate',
      });
      throw error;
    } finally {
      set({ importLoading: false });
    }
  },

  runImportDiagnostics: async (company, file, garageId?, eventId?, options?) => {
    try {
      set({ importLoading: true });
      const service = new ParkingValidationService();
      const diagnostics = await service.runImportDiagnostics(
        company,
        file,
        garageId,
        eventId,
        options
      );

      const summary: ImportSummary = {
        total: diagnostics.length,
        valid: diagnostics.filter((d) => d.isValidImport).length,
        invalid: diagnostics.filter((d) => !d.isValidImport).length,
        new: diagnostics.filter((d) => d.isAdd && d.isValidImport).length,
        updated: diagnostics.filter((d) => d.isUpdate && d.isValidImport).length,
        deleted: diagnostics.filter((d) => d.isDelete && d.isValidImport).length,
      };

      set({ importDiagnostics: diagnostics, importSummary: summary });
    } catch (error) {
      logger.error('Failed to run import diagnostics', error, {
        feature: 'parking-validation',
        route: 'runImportDiagnostics',
      });
      throw error;
    } finally {
      set({ importLoading: false });
    }
  },

  executeImport: async (company, garageId?, eventId?, options?) => {
    try {
      set({ importLoading: true });
      const service = new ParkingValidationService();
      const { importDiagnostics } = get();
      await service.executeImport(company, importDiagnostics, garageId, eventId, options);
      get().clearImportState();
      set((s) => ({ reservationsRefreshToken: s.reservationsRefreshToken + 1 }));
    } catch (error) {
      logger.error('Failed to execute import', error, {
        feature: 'parking-validation',
        route: 'executeImport',
      });
      throw error;
    } finally {
      set({ importLoading: false });
    }
  },

  clearImportState: () =>
    set({
      importDiagnostics: [],
      importSummary: null,
      importLoading: false,
    }),

  // ── Reports ─────────────────────────────────────────────────────────────

  fetchTicketsByMonth: async (month?, options?) => {
    // Last-request-wins: capture this request's token up front and only apply
    // results / clear loading if it's still the latest. Overlapping month
    // fetches (fast prev→current round trip) otherwise let a slower earlier
    // response overwrite the newer month's tickets (PB-4303).
    const requestId = ++reportFetchSeq;
    try {
      set({ reportLoading: true });
      const service = new ParkingValidationService();
      const reportTickets = await service.getTicketsByMonth(month, options);
      if (requestId === reportFetchSeq) {
        set({ reportTickets });
      }
    } catch (error) {
      logger.error('Failed to fetch tickets by month', error, {
        feature: 'parking-validation',
        route: 'fetchTicketsByMonth',
      });
    } finally {
      if (requestId === reportFetchSeq) {
        set({ reportLoading: false });
      }
    }
  },

  exportPdf: async (month?, options?) => {
    try {
      set({ pdfLoading: true });
      const service = new ParkingValidationService();
      return await service.exportPdf(month, options);
    } catch (error) {
      logger.error('Failed to export PDF', error, {
        feature: 'parking-validation',
        route: 'exportPdf',
      });
      throw error;
    } finally {
      set({ pdfLoading: false });
    }
  },

  exportXlsx: async (month?, options?) => {
    try {
      set({ xlsxLoading: true });
      const service = new ParkingValidationService();
      return await service.exportXlsx(month, options);
    } catch (error) {
      logger.error('Failed to export XLSX', error, {
        feature: 'parking-validation',
        route: 'exportXlsx',
      });
      throw error;
    } finally {
      set({ xlsxLoading: false });
    }
  },

  setReportMonth: (month) => set({ reportMonth: month }),

  // ── Programs ────────────────────────────────────────────────────────────

  fetchProgramUsers: async (companyId, discountProgram?, options?) => {
    try {
      set({ programsLoading: true });
      const service = new ParkingValidationService();
      const programUsers = await service.getUsersByDiscountProgram(
        companyId,
        discountProgram,
        options
      );
      set({ programUsers });
    } catch (error) {
      logger.error('Failed to fetch program users', error, {
        feature: 'parking-validation',
        route: 'fetchProgramUsers',
      });
    } finally {
      set({ programsLoading: false });
    }
  },

  assignUser: async (companyId, userId, roles, discountProgram?, options?) => {
    try {
      set({ userManagementLoading: true });
      const service = new ParkingValidationService();
      const result = await service.addUserDiscountProgram(
        companyId,
        userId,
        roles,
        discountProgram,
        options
      );
      await get().fetchProgramUsers(companyId, discountProgram);
      return result;
    } catch (error) {
      logger.error('Failed to assign user', error, {
        feature: 'parking-validation',
        route: 'assignUser',
      });
      throw error;
    } finally {
      set({ userManagementLoading: false });
    }
  },

  updateUserRoles: async (companyId, userId, roles, discountProgram?, options?) => {
    try {
      set({ userManagementLoading: true });
      const service = new ParkingValidationService();
      const result = await service.updateUserDiscountProgramRoles(
        companyId,
        userId,
        roles,
        discountProgram,
        options
      );
      await get().fetchProgramUsers(companyId, discountProgram);
      return result;
    } catch (error) {
      logger.error('Failed to update user roles', error, {
        feature: 'parking-validation',
        route: 'updateUserRoles',
      });
      throw error;
    } finally {
      set({ userManagementLoading: false });
    }
  },

  removeUser: async (companyId, userId, discountProgram?, options?) => {
    try {
      set({ userManagementLoading: true });
      const service = new ParkingValidationService();
      const result = await service.removeUserDiscountProgram(
        companyId,
        userId,
        discountProgram,
        options
      );
      await get().fetchProgramUsers(companyId, discountProgram);
      return result;
    } catch (error) {
      logger.error('Failed to remove user', error, {
        feature: 'parking-validation',
        route: 'removeUser',
      });
      throw error;
    } finally {
      set({ userManagementLoading: false });
    }
  },

  setSelectedProgram: (program) => set({ selectedProgram: program }),

  // ── Usage ───────────────────────────────────────────────────────────────

  fetchSpecialRateUsage: async (options?) => {
    try {
      const service = new ParkingValidationService();
      const specialRateUsage = await service.getSpecialRateUsage(options);
      set({ specialRateUsage });
    } catch (error) {
      logger.error('Failed to fetch special rate usage', error, {
        feature: 'parking-validation',
        route: 'fetchSpecialRateUsage',
      });
    }
  },

  // ── Change Discount ─────────────────────────────────────────────────────

  searchTicketForChange: async (ticketId, options?) => {
    try {
      set({ changeDiscountLoading: true });
      const service = new ParkingValidationService();
      const ticket = await service.searchTicket(undefined, ticketId, undefined, options) as TicketSearchResult;
      set({ changeDiscountTicket: ticket });
      return ticket;
    } catch (error) {
      logger.error('Failed to search ticket for discount change', error, {
        feature: 'parking-validation',
        route: 'searchTicketForChange',
      });
      throw error;
    } finally {
      set({ changeDiscountLoading: false });
    }
  },

  applyNewDiscount: async (request, options?) => {
    try {
      set({ changeDiscountApplying: true });
      const service = new ParkingValidationService();
      const result = await service.addTicket(request, options);
      return result;
    } catch (error) {
      logger.error('Failed to apply new discount', error, {
        feature: 'parking-validation',
        route: 'applyNewDiscount',
      });
      throw error;
    } finally {
      set({ changeDiscountApplying: false });
    }
  },

  clearChangeDiscountState: () =>
    set({
      changeDiscountTicket: null,
      changeDiscountLoading: false,
      changeDiscountApplying: false,
    }),

  // ── Remove Discount ─────────────────────────────────────────────────────

  removeDiscount: async (request, options?) => {
    try {
      set({ removeDiscountLoading: true });
      const service = new ParkingValidationService();
      const result = await service.updateTicket(request, options);
      return result;
    } catch (error) {
      logger.error('Failed to remove discount', error, {
        feature: 'parking-validation',
        route: 'removeDiscount',
      });
      throw error;
    } finally {
      set({ removeDiscountLoading: false });
    }
  },

  // ── Charge to Account ──────────────────────────────────────────────────

  chargeToAccount: async (ticketId, accountId, amount, exitBy, company?, options?) => {
    try {
      set({ chargeToAccountLoading: true });
      const service = new ParkingValidationService();
      const result = await service.chargeToAccount(
        ticketId,
        accountId,
        amount,
        exitBy,
        company,
        options
      );
      return result;
    } catch (error) {
      logger.error('Failed to charge to account', error, {
        feature: 'parking-validation',
        route: 'chargeToAccount',
      });
      throw error;
    } finally {
      set({ chargeToAccountLoading: false });
    }
  },

  getOpenTicketRate: async (ticketId, exitBy, company?, options?) => {
    try {
      const service = new ParkingValidationService();
      return await service.getRateForOpenTicket(ticketId, exitBy, company, options);
    } catch (error) {
      logger.error('Failed to get open-ticket rate', error, {
        feature: 'parking-validation',
        route: 'getOpenTicketRate',
      });
      throw error;
    }
  },

  // ── Validation Session (PB-2292) ────────────────────────────────────────

  searchTicketForValidation: async (ticketNumber?, ticketId?, licensePlate?, options?) => {
    set({ changeDiscountLoading: true });
    try {
      const service = new ParkingValidationService();
      const result = await service.searchTicketNew(ticketNumber, ticketId, licensePlate, options);
      set({
        validationSession: {
          ticket: result ?? null,
          selectedProgram: null,
          chargeToAccountEnabled: Boolean(result?.chargeToAccountEnabled),
        },
      });
      return result ?? null;
    } catch (err) {
      logger.error('searchTicketForValidation failed', err, {
        feature: 'parking-validation',
        route: 'searchTicketForValidation',
      });
      throw err;
    } finally {
      set({ changeDiscountLoading: false });
    }
  },

  setValidationSelectedProgram: (program) =>
    set((s) => ({ validationSession: { ...s.validationSession, selectedProgram: program } })),

  clearValidationSession: () =>
    set({ validationSession: { ticket: null, selectedProgram: null, chargeToAccountEnabled: false } }),
}));
